import {InboundBridgeBucketRecord} from './index';
import {Box, Container, Header, Spinner} from '@amzn/awsui-components-react';
import React from 'react';
import ReactEcharts from 'echarts-for-react';
import {chartColorsLight} from '../common/colors';

const COMPONENT_TEXTS = {
    chart_title: 'Aggregated WAPE Contributions',
    chart_description:
        'The Weighted Absolute Percentage Error (WAPE) contribution metrics have been normalized to align with the total WAPE. Top 10 biggest contributors are shown.',
} as const;

export function InboundBridgePieChart({data, isLoading}: {data: InboundBridgeBucketRecord[]; isLoading: boolean}) {
    const renderData = transformData(data);

    if (isLoading) {
        return (
            <Container header={<Header variant="h2">{COMPONENT_TEXTS.chart_title}</Header>}>
                <Box textAlign="center" color="inherit">
                    <Spinner size="large" />
                </Box>
            </Container>
        );
    }

    return (
        <Container
            header={
                <Header variant="h2" description={COMPONENT_TEXTS.chart_description}>
                    {COMPONENT_TEXTS.chart_title}
                </Header>
            }
        >
            <ReactEcharts
                key={JSON.stringify(renderData)}
                option={getOption()}
                style={{height: '50vh'}}
                opts={{renderer: 'svg'}}
                data-testid="page-pie-chart"
            />
        </Container>
    );

    function getOption() {
        const emSize = parseFloat(getComputedStyle(document.body).fontSize);

        return {
            color: chartColorsLight,
            tooltip: {
                trigger: 'item',
            },
            legend: {
                top: '5%',
                type: 'scroll',
                itemWidth: emSize,
                itemHeight: emSize,
            },
            series: getSeries(renderData),
        };
    }
}

function getSeries(data: {value: number; name: string}[]) {
    return [
        {
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            label: {
                show: true,
                color: '#ffffff',
                position: 'inside',
                formatter: (params) => (params.value * 100 < 1 ? '' : (params.value * 100).toFixed(2) + '%'),
            },
            emphasis: {
                disabled: true,
            },
            labelLine: {
                show: false,
            },
            data,
            itemStyle: {
                borderRadius: 6,
                borderColor: '#fff',
                borderWidth: 2,
            },
        },
    ];
}

export function transformData(records: InboundBridgeBucketRecord[]): {value: number; name: string}[] {
    if (records.length === 0) {
        return [];
    }

    const result = records
        .filter((record) => record.wapeContribRescaled !== undefined && record.wapeContribRescaled > 0)
        .sort((a, b) => b.wapeContribRescaled! - a.wapeContribRescaled!)
        .slice(0, 10)
        .map((record) => {
            return {
                value: record.wapeContribRescaled!,
                name: record.name,
            };
        });

    return result;
}
